import type { SupportedLocale } from './i18n/language-config.ts';
/**
 * Gets the current year
 * @returns Current year as a number (e.g., 2025)
 */
export const getCurrentYear = () => {
    return new Date().getFullYear();
};

/**
 * Gets the previous year
 * @returns Last year as a number (e.g., 2024)
 */
export const getLastYear = () => {
    return new Date().getFullYear() - 1;
};

/**
 * Gets the current year and last year
 * @returns Object with thisYear and lastYear properties (e.g., { thisYear: 2025, lastYear: 2024 })
 */
export const getLastTwoYears = () => {
    return {
        thisYear: getCurrentYear(),
        lastYear: getLastYear(),
    };
};

/**
 * A Date object set to exactly one year ago from now
 */
export const aDateWithTheYearLastYear = new Date(new Date().setFullYear(new Date().getFullYear() - 1));

/**
 * Formats a date using locale-specific formatting
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @param options - Intl.DateTimeFormatOptions for custom formatting
 * @returns Formatted date string (default: 'DD/MM/YYYY' or locale equivalent, e.g., '15.03.2025')
 */
export const formatDate = (
    date?: Date | string | number | null,
    locale?: SupportedLocale,
    options?: Intl.DateTimeFormatOptions,
): string => {
    if (!date) {
        return '';
    }
    const d = date instanceof Date ? date : new Date(date);

    if (options) {
        return d.toLocaleDateString(locale, options);
    }

    // Default short date format
    return d.toLocaleDateString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};

/**
 * Formats a date in short format
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @returns Short date string (e.g., '15.03.2025')
 */
export const formatShortDate = (date?: Date | string | number | null, locale?: SupportedLocale): string =>
    formatDate(date, locale);

/**
 * Formats a date with full weekday name and short date
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @returns Date with weekday (e.g., 'Monday, 15.03.2025')
 */
export const formatDateWithWeekdayLong = (
    date?: Date | string | number | null,
    locale?: SupportedLocale,
): string => {
    if (!date) {
        return '';
    }
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(locale, {
        weekday: 'long',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};

/**
 * Formats the weekday in short format
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @returns Short weekday name (e.g., 'Mon')
 */
export const formatWeekdayShort = (
    date?: Date | string | number | null,
    locale?: SupportedLocale,
): string => {
    if (!date) {
        return '';
    }
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(locale, { weekday: 'short' });
};

/**
 * Formats the month name and day
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @returns Month and day (e.g., 'March 15')
 */
export const formatMonthLongDay = (
    date?: Date | string | number | null,
    locale?: SupportedLocale,
): string => {
    if (!date) {
        return '';
    }
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(locale, { month: 'long', day: 'numeric' });
};

/**
 * Formats a date with full month name, day, and year
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @returns Long date (e.g., 'January 12, 2026' or '12. Januar 2026')
 */
export const formatLongDate = (date?: Date | string | number | null, locale?: SupportedLocale): string => {
    if (!date) {
        return '';
    }
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
};

/**
 * Formats the full weekday name
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @returns Full weekday name (e.g., 'Monday')
 */
export const formatWeekdayLong = (date?: Date | string | number | null, locale?: SupportedLocale): string => {
    if (!date) {
        return '';
    }
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(locale, { weekday: 'long' });
};

/**
 * Formats month and year in short format
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @returns Month and year (e.g., 'Nov 23', 'März 23')
 */
export const formatMonthYearShort = (
    date?: Date | string | number | null,
    locale?: SupportedLocale,
): string => {
    if (!date) {
        return '';
    }
    const d = date instanceof Date ? date : new Date(date);
    const month = d.toLocaleDateString(locale, { month: 'short' });
    const year = d.getFullYear().toString().slice(-2);
    return `${month} ${year}`;
};

/**
 * Formats short month and day
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @returns Short month and day (e.g., 'Jan 12')
 */
export const formatMonthShortDay = (
    date?: Date | string | number | null,
    locale?: SupportedLocale,
): string => {
    if (!date) {
        return '';
    }
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
};

/**
 * Formats short month, day, and year
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'de-DE', 'en-US')
 * @returns Short month, day, and year (e.g., 'Jan 12, 2026')
 */
export const formatMonthShortDayYear = (
    date?: Date | string | number | null,
    locale?: SupportedLocale,
): string => {
    if (!date) {
        return '';
    }
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
};
